import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { usePageContext } from '~/context/usePageContext';

const isElementInViewport = (element: HTMLElement) => {
  return element.getBoundingClientRect().y < window?.innerHeight;
};

const hasScrolledToBottom = () => {
  return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
};

export const useContentGatePopup = ({
  isEnabled,
  useIntersectionLogic,
  contentGateElementRef,
}: {
  isEnabled: boolean;
  useIntersectionLogic: boolean;
  contentGateElementRef?: MutableRefObject<HTMLDivElement | null>;
}) => {
  const { setOpenPageModalType } = usePageContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const updateIntersection = useCallback(() => {
    if (!useIntersectionLogic) {
      if (hasScrolledToBottom()) {
        setIsIntersecting(true);
      } else {
        setIsIntersecting(false);
      }
      return;
    }

    if (!contentGateElementRef?.current) {
      return;
    }

    if (isElementInViewport(contentGateElementRef.current)) {
      setIsIntersecting(true);
    } else {
      setIsIntersecting(false);
    }
  }, [contentGateElementRef, useIntersectionLogic]);

  // Observe when the content gate element scrolls into viewport
  // and update state
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    // Show element if it's already in viewport when the page loads
    if (useIntersectionLogic && contentGateElementRef?.current && isElementInViewport(contentGateElementRef.current)) {
      setIsIntersecting(true);
      return;
    }

    // Show element when user scrolls to the content gate
    window.addEventListener('scroll', updateIntersection);
    return () => {
      window.removeEventListener('scroll', updateIntersection);
    };
  }, [contentGateElementRef?.current, updateIntersection, useIntersectionLogic, isEnabled]);

  // Open popup
  useEffect(() => {
    if (isEnabled && isIntersecting && !isOpen) {
      setOpenPageModalType('content_gate');
      setIsOpen(true);
    }
  }, [isOpen, isIntersecting, isEnabled]);

  // Hide modal when not intersecting
  useEffect(() => {
    if (!isIntersecting && isOpen) {
      setOpenPageModalType(null);
      setIsOpen(false);
    }
  }, [isOpen, isIntersecting]);

  return {
    isOpen: isOpen,
    onClose: () => setIsOpen(false),
  };
};
